import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Checkbox,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import Modal from "../Modal/Modal";
import { AlertInfo } from "../Alerts/Alert";
import { apiUrl } from "../../services/api";
import { useChat } from "./ChatProvider";
import { ChatConversa, corAvatar, iniciaisNome, normalizar } from "./chatTypes";

type Props = {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    grupo?: ChatConversa | null   // preenchido = edição; nulo = criação
    zIndex?: number
};

export default function ModalGrupoChat({ open, setOpen, grupo = null, zIndex = 3000 }: Props) {
    const { contatos, criarGrupo, atualizarGrupo, idUsuarioLogado } = useChat();
    const [nome, setNome] = useState("");
    const [selecionados, setSelecionados] = useState<number[]>([]);
    const [filtro, setFiltro] = useState("");
    const [salvando, setSalvando] = useState(false);

    const edicao = Boolean(grupo);

    useEffect(() => {
        if (!open) return;
        setFiltro("");
        if (grupo) {
            setNome(String(grupo.nome_conversa || ""));
            setSelecionados(
                grupo.participantes
                    .map(p => Number(p.id_usuario))
                    .filter(id => id !== Number(idUsuarioLogado))
            );
        } else {
            setNome("");
            setSelecionados([]);
        }
    }, [open, grupo, idUsuarioLogado]);

    const contatosFiltrados = useMemo(() => {
        const busca = normalizar(filtro);
        if (!busca) return contatos;
        return contatos.filter(u => normalizar(u.nome_usuario).includes(busca));
    }, [contatos, filtro]);

    function alternar(id: number) {
        setSelecionados(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    }

    async function handleSalvar() {
        if (salvando) return;
        if (String(nome).trim() === "") {
            AlertInfo({ icon: 'info', title: 'Informe o nome do grupo' });
            return;
        }
        if (selecionados.length === 0) {
            AlertInfo({ icon: 'info', title: 'Selecione ao menos um participante' });
            return;
        }

        setSalvando(true);
        const ok = grupo
            ? await atualizarGrupo(grupo.id_chat_conversa, nome.trim(), selecionados)
            : await criarGrupo(nome.trim(), selecionados);
        setSalvando(false);

        if (!ok) {
            AlertInfo({ icon: 'error', title: edicao ? 'Falha ao salvar o grupo' : 'Falha ao criar o grupo' });
            return;
        }
        setOpen(false);
    }

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={edicao ? "Editar grupo" : "Novo grupo"}
            maxWidth="xs"
            zIndex={zIndex}
            buttonActions={[
                { color: 'primary', text: salvando ? 'Salvando...' : 'Salvar', handle: handleSalvar },
                { color: 'error', text: 'Cancelar', handle: () => setOpen(false) },
            ]}
        >
            <TextField
                fullWidth
                size="small"
                label="Nome do grupo"
                value={nome}
                onChange={e => setNome(e.target.value)}
                sx={{ mb: 1.5 }}
            />

            <TextField
                fullWidth
                size="small"
                placeholder="Buscar participante..."
                value={filtro}
                onChange={e => setFiltro(e.target.value)}
                InputProps={{
                    sx: { fontSize: 13 },
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 17 }} />
                        </InputAdornment>
                    ),
                }}
            />

            <Typography sx={{ mt: 1, fontSize: 11, color: '#757575' }}>
                {selecionados.length} participante(s) selecionado(s)
            </Typography>

            <Box sx={{ maxHeight: 280, overflowY: 'auto', mt: 0.5 }}>
                {contatosFiltrados.length === 0 && (
                    <Typography sx={{ py: 2, fontSize: 12.5, color: '#9e9e9e', textAlign: 'center' }}>
                        Nenhum usuário encontrado
                    </Typography>
                )}
                <List disablePadding>
                    {contatosFiltrados.map(u => (
                        <ListItemButton key={u.id_usuario} onClick={() => alternar(u.id_usuario)} sx={{ py: 0.4 }}>
                            <Checkbox size="small" checked={selecionados.includes(Number(u.id_usuario))} sx={{ p: 0.5, mr: 0.5 }} />
                            <Avatar
                                src={String(u.photo_usuario || '').length > 0 ? `${apiUrl}/imguploaded/${u.photo_usuario}` : ""}
                                sx={{ width: 28, height: 28, mr: 1, fontSize: 11, bgcolor: corAvatar(u.id_usuario) }}
                            >
                                {iniciaisNome(u.nome_usuario)}
                            </Avatar>
                            <Typography sx={{ fontSize: 12.5 }}>{u.nome_usuario}</Typography>
                        </ListItemButton>
                    ))}
                </List>
            </Box>
        </Modal>
    );
}
